import {
  ADVISORY_DATA_REVIEWED_AT,
  classifyScan,
  DISCLAIMER,
  mapFetchError,
  OFFICIAL_ADVISORIES,
  PHP2SHELL_CVES,
  SAFE_MESSAGES,
  type HeaderBag,
  type ScanReport,
} from '../../../shared/src/index';
import type { AppConfig } from '../config/env';
import { canonicalPublicAddress, isIpLiteral } from '../security/addresses';
import { DestinationRejected, HttpError } from '../security/errors';
import { normalizeInitialUrl, resolveRedirect } from '../security/url-policy';
import type { PerformRequest, ResolveHostname } from '../types';

export interface ScannerDependencies {
  config: Pick<AppConfig, 'timeoutMs' | 'maxRedirects' | 'maxBodyBytes' | 'userAgent'>;
  resolveHostname: ResolveHostname;
  performRequest: PerformRequest;
  now: () => Date;
}

function firstHeader(headers: Record<string, string | string[]>, name: string): string | null {
  const entry = Object.entries(headers).find(([key]) => key.toLowerCase() === name.toLowerCase());
  if (!entry) return null;
  const value = Array.isArray(entry[1]) ? entry[1][0] : entry[1];
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function isRedirectStatus(status: number): boolean {
  return status === 301 || status === 302 || status === 303 || status === 307 || status === 308;
}

async function resolvePin(hostname: string, deps: ScannerDependencies) {
  if (isIpLiteral(hostname)) {
    const pin = canonicalPublicAddress(hostname);
    if (!pin) throw new DestinationRejected(SAFE_MESSAGES.destinationRejected);
    return pin;
  }

  let records;
  try {
    records = await deps.resolveHostname(hostname);
  } catch (error) {
    if (error instanceof DestinationRejected) throw error;
    const mapped = mapFetchError(error);
    throw new DestinationRejected(mapped === SAFE_MESSAGES.timeout ? mapped : SAFE_MESSAGES.dnsFailed);
  }

  if (records.length === 0 || records.length > 16) {
    throw new DestinationRejected(SAFE_MESSAGES.dnsPrivate);
  }

  const pins = records.map((record) => canonicalPublicAddress(record.address));
  if (pins.some((pin) => pin === null)) {
    throw new DestinationRejected(SAFE_MESSAGES.dnsPrivate);
  }

  const pin = pins[0];
  if (!pin) throw new DestinationRejected(SAFE_MESSAGES.dnsPrivate);
  return pin;
}

function buildReport(input: {
  submittedUrl: string;
  finalUrl: string | null;
  scannedAt: string;
  started: number;
  httpStatus: number | null;
  redirectsFollowed: number;
  truncated: boolean;
  strippedPath: boolean;
  headerSets: HeaderBag[];
  failureMessage: string | null;
  unsafeRedirect: boolean;
  tooManyRedirects: boolean;
}): ScanReport {
  const classification = classifyScan({
    headerSets: input.headerSets,
    httpStatus: input.httpStatus,
    failureMessage: input.failureMessage,
    truncated: input.truncated,
    unsafeRedirect: input.unsafeRedirect,
    tooManyRedirects: input.tooManyRedirects,
  });

  const limitations = input.strippedPath
    ? [
        'Only the website root was requested. Any path or query string on the submitted URL was ignored.',
        ...classification.limitations,
      ].slice(0, 20)
    : classification.limitations;

  return {
    submittedUrl: input.submittedUrl,
    finalUrl: input.finalUrl,
    scannedAt: input.scannedAt,
    durationMs: Math.max(0, Date.now() - input.started),
    httpStatus: input.httpStatus,
    ...classification,
    limitations,
    cves: PHP2SHELL_CVES.map((item) => ({ ...item })),
    advisories: OFFICIAL_ADVISORIES.map((item) => ({ ...item })),
    advisoryDataReviewedAt: ADVISORY_DATA_REVIEWED_AT,
    disclaimer: DISCLAIMER,
    redirectsFollowed: input.redirectsFollowed,
    bodyTruncated: input.truncated,
  };
}

export async function runScan(rawUrl: string, deps: ScannerDependencies): Promise<ScanReport> {
  const started = Date.now();
  const scannedAt = deps.now().toISOString();
  const initial = normalizeInitialUrl(rawUrl);

  if (!initial.ok) {
    if (initial.kind === 'invalid') {
      throw new HttpError(400, initial.code, initial.message);
    }
    return buildReport({
      submittedUrl: initial.displayUrl,
      finalUrl: null,
      scannedAt,
      started,
      httpStatus: null,
      redirectsFollowed: 0,
      truncated: false,
      strippedPath: false,
      headerSets: [],
      failureMessage: initial.message,
      unsafeRedirect: false,
      tooManyRedirects: false,
    });
  }

  const submittedUrl = initial.url.href;
  const signal = AbortSignal.timeout(deps.config.timeoutMs);
  const headerSets: HeaderBag[] = [];
  let current = initial.url;
  let redirectsFollowed = 0;
  let truncated = false;
  let httpStatus: number | null = null;
  let finalUrl: string | null = null;

  for (let attempt = 0; attempt <= deps.config.maxRedirects; attempt += 1) {
    if (signal.aborted || Date.now() - started >= deps.config.timeoutMs) {
      return buildReport({
        submittedUrl,
        finalUrl,
        scannedAt,
        started,
        httpStatus,
        redirectsFollowed,
        truncated,
        strippedPath: initial.strippedPath,
        headerSets,
        failureMessage: SAFE_MESSAGES.timeout,
        unsafeRedirect: false,
        tooManyRedirects: false,
      });
    }

    let pin;
    try {
      pin = await resolvePin(current.hostname, deps);
    } catch (error) {
      const message = error instanceof DestinationRejected ? error.message : SAFE_MESSAGES.dnsFailed;
      return buildReport({
        submittedUrl,
        finalUrl,
        scannedAt,
        started,
        httpStatus,
        redirectsFollowed,
        truncated,
        strippedPath: initial.strippedPath,
        headerSets,
        failureMessage: message,
        unsafeRedirect: false,
        tooManyRedirects: false,
      });
    }

    let response;
    try {
      response = await deps.performRequest({
        url: current,
        ip: pin.address,
        family: pin.family,
        timeoutMs: Math.max(1, deps.config.timeoutMs - (Date.now() - started)),
        maxBodyBytes: deps.config.maxBodyBytes,
        userAgent: deps.config.userAgent,
        signal,
      });
    } catch (error) {
      return buildReport({
        submittedUrl,
        finalUrl,
        scannedAt,
        started,
        httpStatus,
        redirectsFollowed,
        truncated,
        strippedPath: initial.strippedPath,
        headerSets,
        failureMessage: mapFetchError(error),
        unsafeRedirect: false,
        tooManyRedirects: false,
      });
    }

    finalUrl = current.href;
    httpStatus = response.statusCode;
    truncated = truncated || response.truncated;
    headerSets.push(response.headers);

    if (!isRedirectStatus(response.statusCode)) {
      return buildReport({
        submittedUrl,
        finalUrl,
        scannedAt,
        started,
        httpStatus,
        redirectsFollowed,
        truncated,
        strippedPath: initial.strippedPath,
        headerSets,
        failureMessage: null,
        unsafeRedirect: false,
        tooManyRedirects: false,
      });
    }

    const location = firstHeader(response.headers, 'location');
    if (!location || attempt === deps.config.maxRedirects) {
      return buildReport({
        submittedUrl,
        finalUrl,
        scannedAt,
        started,
        httpStatus,
        redirectsFollowed,
        truncated,
        strippedPath: initial.strippedPath,
        headerSets,
        failureMessage: location ? SAFE_MESSAGES.tooManyRedirects : SAFE_MESSAGES.badRedirect,
        unsafeRedirect: false,
        tooManyRedirects: Boolean(location),
      });
    }

    const next = resolveRedirect(current, location);
    if (!next.ok) {
      return buildReport({
        submittedUrl,
        finalUrl,
        scannedAt,
        started,
        httpStatus,
        redirectsFollowed,
        truncated,
        strippedPath: initial.strippedPath,
        headerSets,
        failureMessage: SAFE_MESSAGES.redirectBlocked,
        unsafeRedirect: true,
        tooManyRedirects: false,
      });
    }

    current = next.url;
    redirectsFollowed += 1;
  }

  return buildReport({
    submittedUrl,
    finalUrl,
    scannedAt,
    started,
    httpStatus,
    redirectsFollowed,
    truncated,
    strippedPath: initial.strippedPath,
    headerSets,
    failureMessage: SAFE_MESSAGES.tooManyRedirects,
    unsafeRedirect: false,
    tooManyRedirects: true,
  });
}
